import { useUser } from '@folklore/auth';
import { loadGoogleGsi } from '@folklore/services';
import { createContext, ReactNode, use, useEffect, useState } from 'react';

import { useIsCheckingAuth } from './AuthContext';

interface GoogleContextValue {
    clientId: string | null;
    sdk: typeof google.accounts | null;
    token: string | null;
    setRequiresSdk: (requiresSdk: boolean) => void;
}

export const GoogleContext = createContext<GoogleContextValue>({
    clientId: null,
    sdk:
        typeof window !== 'undefined' &&
        typeof window.google !== 'undefined' &&
        typeof window.google.accounts !== 'undefined'
            ? (window.google.accounts as typeof google.accounts)
            : null,
    token: null,
    setRequiresSdk: () => {},
});

export function useGoogleContext() {
    const context = use(GoogleContext);
    return context;
}

export function useGoogleSdk() {
    const { sdk = null, setRequiresSdk } = useGoogleContext();
    useEffect(() => {
        setRequiresSdk(true);
    }, [setRequiresSdk]);
    return sdk;
}

export function useGoogleToken() {
    const { token = null } = useGoogleContext();
    return token;
}

interface GoogleContextProviderProps {
    clientId?: string | null;
    children: ReactNode;
}

export function GoogleContextProvider({ clientId = null, children }: GoogleContextProviderProps) {
    const isCheckingAuth = useIsCheckingAuth();
    const user = useUser();
    const [requiresSdk, setRequiresSdk] = useState(false);
    const [sdk, setSdk] = useState<typeof google.accounts | null>(
        typeof window !== 'undefined' &&
            typeof window.google !== 'undefined' &&
            typeof window.google.accounts !== 'undefined' &&
            typeof window.google.accounts.id !== 'undefined'
            ? (window.google.accounts as typeof google.accounts)
            : null,
    );
    const [token, setToken] = useState<string | null>(null);

    useEffect(() => {
        if (sdk !== null || isCheckingAuth || (user !== null && !requiresSdk)) {
            return () => {};
        }

        let canceled = false;
        loadGoogleGsi().then((newSdk: typeof google.accounts) => {
            if (!canceled) {
                setSdk(newSdk);
            }
            newSdk.id.initialize({
                client_id: clientId,
                callback: (response: { credential?: string }) => {
                    setToken(response.credential || null);
                },
            });
        });
        return () => {
            canceled = true;
        };
    }, [sdk, clientId, isCheckingAuth, user, requiresSdk]);

    const value = {
        clientId,
        sdk,
        token,
        setRequiresSdk,
    };

    return <GoogleContext value={value}>{children}</GoogleContext>;
}
